import { Input } from "../shared/physics";
import { padMap, rumbleScale } from "./options";

const keys = new Set<string>();
// ---- gamepad (standard mapping): stick / D-pad steer, A or RT gas, B or LT brake, X / Y / RB jet; menus: D-pad or stick + A / Start (Enter), B / Select (Escape) ----
let padOnPress: (code: string) => void = () => {};
let pad = { accel: false, brake: false, jet: false, left: false, right: false };
let activePad: Gamepad | null = null;
export const padInfo = { name: "", since: 0 };
const padHeld = new Map<string, { at: number; rep: number }>();
addEventListener("gamepadconnected", (e) => ((padInfo.name = (e as GamepadEvent).gamepad.id), (padInfo.since = performance.now())));
addEventListener("gamepaddisconnected", () => ((padInfo.name = ""), (activePad = null), (pad = { accel: false, brake: false, jet: false, left: false, right: false }), setPadMode(false)));
export const padConnected = () => !!activePad;
/**
 * A gamepad is connected and in use: the on-screen touch controls go away (CSS, body[data-pad]). A touch on the screen brings them back,
 * and the next press on the pad hides them again.
 */
function setPadMode(on: boolean) {
  if (typeof document !== "undefined" && document.body && document.body.dataset.pad !== (on ? "1" : "0")) document.body.dataset.pad = on ? "1" : "0";
}
addEventListener("pointerdown", (e) => { if (e.pointerType === "touch") setPadMode(false); }, { capture: true, passive: true });
/** Fires `code` when `down` becomes true, then repeats while held (menus). */
function edge(id: string, down: boolean, code: string, now: number, repeat: boolean) {
  const h = padHeld.get(id);
  if (!down) return void padHeld.delete(id);
  if (!h) {
    padHeld.set(id, { at: now, rep: now + 380 });
    padOnPress(code);
  } else if (repeat && now >= h.rep) {
    h.rep = now + 130;
    padOnPress(code);
  }
}
/** The next button the player presses is handed to `cb` (buttons already down when asked must be released first; SELECT, START and the D-pad cannot be assigned). */
let capture: ((btn: number | null) => void) | null = null;
let captureSince = 0;
export function capturePadButton(cb: ((btn: number | null) => void) | null) {
  capture = cb;
  captureSince = performance.now();
}
export const padCapturing = () => capture !== null;
/** Call every animation frame. `inRace`: the pad drives the car (menu presses are mostly off so B/LT can brake). */
export function pollGamepad(inRace: boolean, now: number) {
  const list = navigator.getGamepads ? [...navigator.getGamepads()] : [];
  const gp = list.find((p) => p && p.connected) ?? null;
  activePad = gp;
  if (!gp) return void setPadMode(false);
  const b = (i: number) => !!gp.buttons[i]?.pressed;
  const v = (i: number) => gp.buttons[i]?.value ?? 0;
  const ax = gp.axes[0] ?? 0, ay = gp.axes[1] ?? 0;
  const L = ax < -0.35 || b(14), R = ax > 0.35 || b(15), U = ay < -0.55 || b(12), D = ay > 0.55 || b(13);
  const on = (i: number) => b(i) || v(i) > 0.15; // triggers are analogue
  if (gp.buttons.some((x) => x?.pressed || x?.value > 0.5) || Math.abs(ax) > 0.5 || Math.abs(ay) > 0.5) setPadMode(true); // the pad is being used
  if (capture) {
    // reassigning: the next new press is taken (menus stay quiet meanwhile); after 8 s the question is dropped
    const down = gp.buttons.map((x, i) => (x?.pressed || x?.value > 0.15 ? i : -1)).filter((i) => i >= 0);
    for (const i of down) if (!padHeld.has("cap" + i)) {
      if (now - captureSince < 400) padHeld.set("cap" + i, { at: now, rep: Infinity }); // held since before the question
      else if (i !== 8 && i !== 9 && (i < 12 || i > 15)) {
        const cb = capture;
        capture = null;
        for (const j of down) padHeld.set("cap" + j, { at: now, rep: Infinity });
        for (const id of ["a", "s", "b"]) padHeld.set(id, { at: now, rep: Infinity });
        return void cb(i);
      }
    }
    for (const k of [...padHeld.keys()]) if (k.startsWith("cap") && !down.includes(Number(k.slice(3)))) padHeld.delete(k);
    if (now - captureSince > 8000) {
      const cb = capture;
      capture = null;
      cb(null);
    }
    return;
  }
  for (const k of [...padHeld.keys()]) if (k.startsWith("cap")) padHeld.delete(k);
  pad = { accel: padMap.accel.some(on), brake: padMap.brake.some(on), jet: padMap.jet.some(on), left: L, right: R };
  if (inRace) {
    // buttons held while driving must not act as menu presses when the race ends: mark them as already held
    const held: [string, boolean][] = [["u", U], ["d", D], ["l", L], ["r", R], ["a", b(0)], ["s", b(9)], ["b", b(1) || b(8)]];
    for (const [id, on] of held) on ? padHeld.set(id, { at: now, rep: Infinity }) : padHeld.delete(id);
    edge("sel", b(8), "Escape", now, false); // Select / Back leaves the race
    return;
  }
  padHeld.delete("sel");
  edge("u", U, "ArrowUp", now, true);
  edge("d", D, "ArrowDown", now, true);
  edge("l", L, "ArrowLeft", now, true);
  edge("r", R, "ArrowRight", now, true);
  edge("a", b(0), "Enter", now, false);
  edge("s", b(9), "Enter", now, false);
  edge("b", b(1) || b(8), "Escape", now, false);
}
/** Short rumble (collisions, walls) on pads that support it. */
let lastRumble = 0;
export function rumble(ms: number, strong = 0.5, weak = 0.3, force = false) {
  const k = rumbleScale(); // the player's option: OFF / LOW / NORMAL / HIGH
  if (k <= 0) return;
  const now = performance.now();
  if (!force && now - lastRumble < 220) return; // scraping a wall must not buzz continuously
  lastRumble = now;
  try {
    const act = (activePad as any)?.vibrationActuator;
    act?.playEffect?.("dual-rumble", { startDelay: 0, duration: Math.round(ms * Math.min(k, 1.4)), weakMagnitude: Math.min(1, weak * k), strongMagnitude: Math.min(1, strong * k) })?.catch?.(() => {});
  } catch {
    /* unsupported */
  }
}
const touch = new Set<string>();
const zone = new Set<string>(); // the full-screen touch layout
const MAP: Record<string, string> = {
  ArrowLeft: "left", ArrowRight: "right", ArrowUp: "jet", ArrowDown: "brake",
  KeyZ: "accel", Space: "accel", KeyX: "brake", KeyA: "accel", KeyQ: "left", KeyD: "right", KeyC: "jet",
};
let releaseStick = () => {};
/** Slide-pad: drag the thumb left/right of the centre to steer (a small dead zone keeps it straight). */
function bindStick(el: HTMLElement) {
  const knob = el.querySelector<HTMLElement>(".knob")!;
  let id = -1;
  releaseStick = () => { id = -1; touch.delete("left"); touch.delete("right"); el.classList.remove("on"); knob.style.transform = ""; };
  const set = (e: PointerEvent) => {
    const r = el.getBoundingClientRect();
    const dx = (e.clientX - (r.left + r.width / 2)) / (r.width / 2); // -1..1
    const k = Math.max(-1, Math.min(1, dx));
    touch.delete("left"); touch.delete("right");
    if (k < -0.16) touch.add("left");
    else if (k > 0.16) touch.add("right");
    knob.style.transform = `translateX(${k * r.width * 0.33}px)`;
  };
  const end = (e: PointerEvent) => {
    if (e.pointerId !== id) return;
    id = -1; touch.delete("left"); touch.delete("right"); el.classList.remove("on"); knob.style.transform = "";
  };
  // a new finger always takes over: a stale one (its "up" never reached us) must not leave the stick frozen
  el.addEventListener("pointerdown", (e) => { e.preventDefault(); id = e.pointerId; try { el.setPointerCapture(id); } catch { /* synthetic pointer */ } el.classList.add("on"); set(e); });
  el.addEventListener("pointermove", (e) => { if (e.pointerId === id) set(e); });
  for (const t of ["pointerup", "pointercancel", "lostpointercapture"]) el.addEventListener(t, end as EventListener);
  for (const t of ["pointerup", "pointercancel"]) addEventListener(t, end as EventListener); // also when the finger is lifted elsewhere
}
/** Releases everything held on the full-screen layout (the race ended, or the screen changed under a finger). */
export function clearZones() {
  releaseZones();
  releaseStick();
  zone.clear();
  document.querySelectorAll<HTMLElement>(".tz").forEach((e) => (e.style.display = "none"));
}
/**
 * Full-screen touch layout: a finger down on the left half steers by sliding left/right from where it landed (the reference follows the finger,
 * so reversing is quick); a finger down on the right half is the gas, sliding up adds the JET, sliding down brakes. No button to aim at.
 */
let releaseZones = () => {};
function bindZones(el: HTMLElement) {
  const ptrs = new Map<number, { side: "l" | "r"; ox: number; oy: number; ring: HTMLElement; dot: HTMLElement }>();
  const mk = (side: "l" | "r") => {
    const ring = document.createElement("div");
    ring.className = "tz " + side;
    ring.innerHTML = side === "l" ? "<i>◀</i><i>▶</i>" : "<i>JET</i><i>BRAKE</i>";
    const dot = document.createElement("b");
    ring.appendChild(dot);
    el.appendChild(ring);
    return { ring, dot };
  };
  const rings = { l: mk("l"), r: mk("r") };
  const apply = (p: { side: "l" | "r"; ox: number; oy: number; ring: HTMLElement; dot: HTMLElement }, e: PointerEvent) => {
    if (p.side === "l") {
      const R = 46;
      if (e.clientX - p.ox > R) p.ox = e.clientX - R;
      if (e.clientX - p.ox < -R) p.ox = e.clientX + R;
      const dx = e.clientX - p.ox;
      zone.delete("left"); zone.delete("right");
      if (dx < -10) zone.add("left");
      else if (dx > 10) zone.add("right");
      p.dot.style.transform = `translate(${dx}px,0)`;
    } else {
      const R = 56;
      if (e.clientY - p.oy > R) p.oy = e.clientY - R;
      if (e.clientY - p.oy < -R) p.oy = e.clientY + R;
      const dy = e.clientY - p.oy;
      zone.add("accel");
      if (dy < -26) zone.add("jet"); else zone.delete("jet");
      if (dy > 26) zone.add("brake"); else zone.delete("brake");
      p.dot.style.transform = `translate(0,${dy}px)`;
    }
    p.ring.style.left = p.ox + "px";
    p.ring.style.top = p.oy + "px";
  };
  const drop = (id: number) => {
    const p = ptrs.get(id);
    if (!p) return;
    ptrs.delete(id);
    p.ring.style.display = "none";
    if (p.side === "l") (zone.delete("left"), zone.delete("right"));
    else (zone.delete("accel"), zone.delete("jet"), zone.delete("brake"));
  };
  const end = (e: PointerEvent) => drop(e.pointerId);
  // whatever the reason a finger is lost (layer hidden under it, screen change, system gesture, app switch), nothing stays held
  releaseZones = () => [...ptrs.keys()].forEach(drop);
  for (const t of ["pointerup", "pointercancel"]) addEventListener(t, end as EventListener);
  addEventListener("touchend", (e) => { if (!e.touches.length) releaseZones(); }, { passive: true });
  addEventListener("touchcancel", () => releaseZones(), { passive: true });
  addEventListener("blur", () => releaseZones());
  document.addEventListener("visibilitychange", () => { if (document.hidden) releaseZones(); });
  el.addEventListener("pointerdown", (e) => {
    e.preventDefault();
    const side = document.body.dataset.touch === "2" || e.clientX < innerWidth / 2 ? "l" : "r"; // floating-stick mode: the whole screen steers, the buttons sit above it
    for (const [id, q] of [...ptrs]) if (q.side === side) drop(id); // one finger per side: the newest takes over (an older one may be a stale pointer that never got its "up")
    const { ring, dot } = rings[side];
    const p = { side, ox: e.clientX, oy: e.clientY, ring, dot } as const;
    ptrs.set(e.pointerId, { ...p });
    try { el.setPointerCapture(e.pointerId); } catch { /* synthetic pointer */ }
    ring.style.display = "block";
    apply(ptrs.get(e.pointerId)!, e);
  });
  el.addEventListener("pointermove", (e) => { const p = ptrs.get(e.pointerId); if (p) apply(p, e); });
  for (const t of ["pointerup", "pointercancel", "lostpointercapture"]) el.addEventListener(t, end as EventListener);
}
export function bindInput(onPress: (code: string) => void) {
  padOnPress = onPress;
  addEventListener("keydown", (e) => {
    if ((e.target as HTMLElement | null)?.tagName === "INPUT") return; // typing in the name field
    if (MAP[e.code] || e.code === "Enter" || e.code === "Escape") e.preventDefault();
    if (!keys.has(e.code)) onPress(e.code);
    keys.add(e.code);
  });
  // always released, even when the key went up in the name field (a key pressed to open it would stay "held" and never fire again)
  addEventListener("keyup", (e) => keys.delete(e.code));
  addEventListener("blur", () => keys.clear());
  const zones = document.getElementById("zones");
  if (zones) bindZones(zones);
  const stick = document.getElementById("stick");
  if (stick) bindStick(stick);
  document.querySelectorAll<HTMLElement>("[data-btn]").forEach((el) => {
    const b = el.dataset.btn!;
    const on = (e: Event) => (e.preventDefault(), touch.add(b), onPress("btn:" + b));
    const off = (e: Event) => (e.preventDefault(), touch.delete(b));
    el.addEventListener("pointerdown", on);
    el.addEventListener("pointerup", off);
    el.addEventListener("pointerleave", off);
    el.addEventListener("pointercancel", off);
  });
}
// Which kind of control drives a run: counted frame by frame, the most used one wins (0 keyboard, 1 touch, 2 gamepad).
const srcFrames = [0, 0, 0];
export const resetSource = () => srcFrames.fill(0);
export function sampleSource() {
  if (touch.size || zone.size) srcFrames[1]++;
  else if (pad.accel || pad.brake || pad.jet || pad.left || pad.right) srcFrames[2]++;
  else if ([...keys].some((k) => MAP[k])) srcFrames[0]++;
}
export const runSource = (): number | undefined => (srcFrames.some((n) => n > 0) ? srcFrames.indexOf(Math.max(...srcFrames)) : undefined);
export function readInput(): Input {
  const has = (n: string) => touch.has(n) || zone.has(n) || [...keys].some((k) => MAP[k] === n);
  // JET also gives gas, so ↑ (or C) + a direction is enough: three keys at once (Z + ↑ + ←) are dropped by many keyboards ("ghosting"), typically on one side only
  return { accel: has("accel") || has("jet") || pad.accel || pad.jet,
    brake: has("brake") || pad.brake, jet: has("jet") || pad.jet, left: has("left") || pad.left, right: has("right") || pad.right };
}
